import { useState, useEffect, useCallback, ChangeEvent } from "react";
import clsx from "clsx";

import { DayPicker } from "react-day-picker";

import Transition from "../Transition";
import FieldLabel from "../Field-Label";

import { Props } from "./types";

const modifiersStyles = {
  selected: { backgroundColor: "#5b1eb7", color: "#ffffff" },
};

const convertToDate = (
  value: string | number | Date | null | undefined
): Date | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === "string" || typeof value === "number") {
    value = new Date(value);
  }
  if (value instanceof Date) {
    return isNaN(value.valueOf()) ? undefined : value;
  }
  return undefined;
};

const DatePicker = ({
  name,
  label,
  labelTags,
  required,
  placeholder = "Select Date",
  help,
  error,
  ...controls
}: Props) => {
  const [focussed, setFocussed] = useState(false);
  const ref = "inputRef" in controls ? controls.inputRef : undefined;

  const [selectedDate, setSelectedDate] = useState(
    "value" in controls ? convertToDate(controls.value) : undefined
  );

  useEffect(() => {
    if ("value" in controls) {
      const value = convertToDate(controls.value);
      if (value?.valueOf() !== selectedDate?.valueOf()) {
        setSelectedDate(value);
      }
    }
  }, [controls, selectedDate, setSelectedDate]);

  useEffect(() => {
    const current = ref?.current;
    if (current) {
      current.value = selectedDate?.toDateString() || "";
    }
  }, [ref, selectedDate]);

  const inputValue = selectedDate?.toDateString() || "";

  const focus = useCallback(() => setFocussed(true), [setFocussed]);
  const blur = useCallback(() => setFocussed(false), [setFocussed]);

  // @ts-ignore
  const controlOnChange = controls.onChange as (d: Date | null) => unknown;

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const c = new Date(e.target.value);
      setSelectedDate(isNaN(c.valueOf()) ? undefined : c);
      controlOnChange(isNaN(c.valueOf()) ? null : c);
    },
    [setSelectedDate, controlOnChange]
  );

  const onDayClick = useCallback(
    (d: Date) => {
      controlOnChange(d);
      setSelectedDate(d);
    },
    [setSelectedDate, controlOnChange]
  );

  return (
    <div onFocus={focus} onBlur={blur} className="relative">
      {label && (
        <FieldLabel
          name={name}
          label={label}
          focussed={focussed}
          error={!!error}
          required={required}
          tags={labelTags}
        />
      )}
      <input
        value={inputValue}
        onChange={onChange}
        ref={ref}
        onFocus={focus}
        placeholder={placeholder}
        aria-label={
          typeof label === "string" ? label : label ? undefined : "Pick a Date"
        }
        className={clsx(
          "border-b-2",
          "border-gray-300",
          "hover:border-gray-400",
          "focus:border-primary-main",
          "outline-none",
          "text-gray-800",
          "pl-2",
          "my-1",
          "w-full"
        )}
      />
      <Transition
        show={focussed}
        id={`${name}-controls`}
        tag="ul"
        role="dialog"
        aria-label={
          typeof label === "string"
            ? `${label}-calendar`
            : label
            ? undefined
            : "Calendar"
        }
        className={
          "pl-1 absolute top-full w-full bg-white z-50 overflow-visible h-64"
        }
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <DayPicker
          mode="uncontrolled"
          modifierStyles={modifiersStyles}
          onDayClick={onDayClick}
          selected={selectedDate}
          month={selectedDate}
          // @ts-ignore
          classNames={{ day: "text-sm" }}
        />
      </Transition>

      {help && (
        <div
          className={clsx(
            "text-xs",
            !!error ? "text-error-dark" : "text-gray-700"
          )}
        >
          {help}
        </div>
      )}
      {error && <div className="text-error-dark text-xs">{error}</div>}
    </div>
  );
};

export default DatePicker;
